import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqData = [
  {
    question: "What is an end-of-life doula?",
    answer: "An end-of-life doula (also called a death doula) provides non-medical support to individuals and families facing end-of-life. This includes emotional support, practical guidance, advance care planning, legacy work, and presence during the dying process. We complement hospice and medical care by focusing on the holistic needs of the person and their loved ones."
  },
  {
    question: "How is this different from hospice?",
    answer: "Hospice provides medical care for those with a terminal diagnosis, including pain management and symptom control. As an end-of-life doula, I provide non-medical support that complements hospice care. I focus on emotional, practical, and spiritual support, advance care planning, legacy projects, and being present with families during this sacred time. Many families use both services together."
  },
  {
    question: "What areas do you serve?",
    answer: "I serve individuals and families throughout Columbia, Missouri and the greater Mid-Missouri region. I offer both in-person and virtual support options to accommodate different needs and situations."
  },
  {
    question: "When should I reach out?",
    answer: "There's no wrong time to reach out. Some people contact me when they receive a serious diagnosis, while others reach out for advance care planning while still healthy. I also support families who are actively caring for a loved one who is dying. The earlier we connect, the more time we have to build a relationship and plan together."
  },
  {
    question: "How much do your services cost?",
    answer: "Payment for my services are thoughtfully tailored to your individual needs and circumstances.  Your first 30-minute consultation is complimentary, during this call we can discuss pricing options that meet your needs and level of support required."
  },
  {
    question: "Do you work with hospice?",
    answer: "Yes, I work collaboratively with hospice teams. My services complement hospice care by providing additional emotional support, presence, and practical assistance that hospice staff may not have time to offer. I communicate with the hospice team to ensure coordinated care."
  },
  {
    question: "What if I'm not sure what I need?",
    answer: "That's completely okay. Many people aren't sure where to start, which is why I offer a free initial consultation. We'll talk about your situation, your concerns, and what kind of support might be helpful. There's no pressure to commit to anything."
  },
  {
    question: "Can you help with advance care planning if I'm healthy?",
    answer: "Absolutely. In fact, the best time to do advance care planning is while you're healthy and thinking clearly. I can guide you through creating your advance directive, healthcare power of attorney, and living will so your wishes are documented and your loved ones know what you want."
  },
];

const FAQ = () => {
  // Generate FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>FAQ | End-of-Life Doula Columbia, MO | Serenity Passage</title>
        <meta
          name="description"
          content="Frequently asked questions about end-of-life doula services in Columbia, Missouri. Learn about death doula support and how Serenity Passage helps families in Mid-Missouri."
        />
        <meta
          name="keywords"
          content="end-of-life doula FAQ Columbia MO, death doula questions Mid-Missouri, hospice vs doula, advance care planning Columbia Missouri"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/faq" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-rose-100 to-rose-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif text-gray-800 text-center mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Answers to common questions about end-of-life doula services
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle className="w-8 h-8 text-rose-600" />
            <h2 className="text-2xl font-serif text-gray-800">Common Questions</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-rose-50 rounded-xl px-6 border-none"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-medium text-gray-800 hover:text-rose-600 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-rose-50 to-rose-100 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-serif text-gray-800 mb-4">
              Have More Questions?
            </h3>
            <p className="text-gray-600 mb-6">
              I'm here to help. Schedule a free consultation and let's talk.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Contact Me
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
